import { ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, ZAxis, Tooltip, CartesianGrid, Label, Cell } from 'recharts';
import type { CompassRecord } from '../../types';
import { tooltipStyles } from '../../utils/chartStyles';
import { SEGMENT_COLORS } from '../../constants';
import Select, { type StylesConfig } from 'react-select';

interface CompassChartProps {
  data: CompassRecord[];
  locationOptions: string[];
  selectedLocation: string;
  onLocationChange: (location: string) => void;
}

const selectStyles: StylesConfig<{ value: string; label: string }, false> = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: 'rgba(8,11,22,0.85)',
    borderColor: state.isFocused ? 'rgba(159,178,255,0.8)' : 'rgba(255,255,255,0.12)',
    boxShadow: 'none',
    minHeight: 44,
    cursor: 'pointer',
    color: '#f6f9ff'
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: '#070b18',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 12,
    overflow: 'hidden'
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? 'rgba(124,134,255,0.35)'
      : state.isFocused
        ? 'rgba(124,134,255,0.15)'
        : 'transparent',
    color: '#f6f9ff',
    cursor: 'pointer'
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#f6f9ff'
  }),
  input: (provided) => ({
    ...provided,
    color: '#f6f9ff'
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#9fa5bf'
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '2px 8px'
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: '#cfd4ee'
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    backgroundColor: 'rgba(255,255,255,0.1)'
  })
};

const CompassChart = ({ data, locationOptions, selectedLocation, onLocationChange }: CompassChartProps) => {
  const coloredData = data.map((d) => {
    let color = '#6c738d'; // neutral gray
    if (d.x >= 0.2) {
      color = SEGMENT_COLORS.Right;
    } else if (d.x <= -0.2) {
      color = SEGMENT_COLORS.Left;
    }
    return { ...d, color };
  });

  return (
    <div className="chart-wrapper">
      <div className="chart-controls stacked">
        <Select
          className="location-select"
          classNamePrefix="select"
          styles={selectStyles}
          options={locationOptions.map((loc) => ({ value: loc, label: loc }))}
          value={{ value: selectedLocation, label: selectedLocation }}
          onChange={(option) => option && onLocationChange(option.value)}
          placeholder="Search locations"
        />
      </div>
      <ResponsiveContainer width="100%" height={500}>
        <ScatterChart margin={{ top: 16, right: 24, left: 16, bottom: 32 }}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
          <XAxis type="number" dataKey="x" domain={[-1, 1]} tick={{ fill: '#dfe6ff' }}>
            <Label value="Polarity (Left to Right)" position="insideBottom" offset={-20} fill="#cfd4ee" />
          </XAxis>
          <YAxis type="number" dataKey="y" domain={[0, 1]} tick={{ fill: '#dfe6ff' }}>
            <Label value="Objectivity (Low to High)" angle={-90} position="insideLeft" offset={-5} fill="#cfd4ee" />
          </YAxis>
          <ZAxis type="number" dataKey="count" range={[80, 400]} />
          <Tooltip
            cursor={{ stroke: '#9fb2ff', strokeDasharray: '3 3' }}
            formatter={(value: number, name: string) => {
              const labels: Record<string, string> = { x: 'Polarity', y: 'Objectivity', count: 'Users' };
              return [value, labels[name] ?? name];
            }}
            {...tooltipStyles}
          />
          <Scatter data={coloredData} shape="circle" opacity={0.8} fill="#9fb2ff">
            {coloredData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>
      <p className="footnote">Higher Y = more factual; X spans left (-1) to right (+1) polarity.</p>
    </div>
  );
};

export default CompassChart;
